import React, { useState } from 'react';
import {
    Box,
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Text,
    VStack,
    HStack,
    IconButton,
    useDisclosure,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Input,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { format } from 'date-fns';
import CustomFieldForm from './CustomFieldForm';
import JournalEntryForm from './JournalEntryForm';
import JournalEntryPreview from './JournalEntryPreview';

interface CustomField {
    name: string;
    field_type: 'text' | 'number' | 'boolean' | 'scale' | 'time_duration';
    category: 'habit' | 'metric' | 'medication' | 'mood' | 'note';
    unit?: string;
    target_value?: {
        min?: number;
        max?: number;
        target?: number;
    };
    frequency: {
        type: 'daily' | 'weekly' | 'monthly';
        times_per_period: number;
    };
    show_in_new_entries: boolean;
    is_archived: boolean;
    entries_count: number;
    is_new: boolean;
}

interface Tag {
    name: string;
    color: string;
    entries_count: number;
    is_new: boolean;
}

interface JournalEntry {
    content: string;
    entry_date: string;
    mood_label?: string;
    tags?: string[];
    custom_fields?: Array<{
        name: string;
        value: string;
        field_type?: string;
        category?: string;
        unit?: string;
    }>;
}

interface ImportPreview {
    entries: JournalEntry[];
    stats: {
        totalEntries: number;
        tags: Tag[];
        customFields: CustomField[];
        uniqueMoods: string[];
    };
    dateRange: {
        start: string;
        end: string;
    };
}

interface JournalImportPreviewProps {
    isOpen: boolean;
    onClose: () => void;
    preview: ImportPreview;
    onConfirm: (preview: ImportPreview) => void;
}

const JournalImportPreview: React.FC<JournalImportPreviewProps> = ({
    isOpen,
    onClose,
    preview,
    onConfirm
}) => {
    const [editedPreview, setEditedPreview] = useState<ImportPreview>(preview);
    const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
    const [selectedCustomField, setSelectedCustomField] = useState<CustomField | null>(null);
    const entryModal = useDisclosure();
    const customFieldModal = useDisclosure();

    // Entry editing
    const handleEditEntry = (entry: JournalEntry) => {
        setSelectedEntry({ ...entry });
        entryModal.onOpen();
    };

    const handleSaveEntry = () => {
        if (!selectedEntry) return;

        setEditedPreview(prev => ({
            ...prev,
            entries: prev.entries.map(entry =>
                entry.entry_date === selectedEntry.entry_date ? selectedEntry : entry
            )
        }));
        entryModal.onClose();
    };

    // Custom field editing
    const handleEditCustomField = (field: CustomField) => {
        setSelectedCustomField({ ...field });
        customFieldModal.onOpen();
    };

    const handleSaveCustomField = () => {
        if (!selectedCustomField) return;

        setEditedPreview(prev => ({
            ...prev,
            stats: {
                ...prev.stats,
                customFields: prev.stats.customFields.map(field =>
                    field.name === selectedCustomField.name ? selectedCustomField : field
                )
            }
        }));
        customFieldModal.onClose();
    };

    // Tag management
    const handleRemoveTag = (tagName: string) => {
        setEditedPreview(prev => ({
            ...prev,
            stats: {
                ...prev.stats,
                tags: prev.stats.tags.filter(t => t.name !== tagName)
            },
            entries: prev.entries.map(entry => ({
                ...entry,
                tags: entry.tags?.filter(t => t !== tagName)
            }))
        }));
    };

    // Custom field removal
    const handleRemoveCustomField = (fieldName: string) => {
        setEditedPreview(prev => ({
            ...prev,
            stats: {
                ...prev.stats,
                customFields: prev.stats.customFields.filter(f => f.name !== fieldName)
            },
            entries: prev.entries.map(entry => ({
                ...entry,
                custom_fields: entry.custom_fields?.filter(f => f.name !== fieldName)
            }))
        }));
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="6xl">
            <ModalOverlay />
            <ModalContent maxW="90vw">
                <ModalHeader>Import Preview</ModalHeader>
                <ModalBody>
                    <Tabs>
                        <TabList>
                            <Tab>Summary</Tab>
                            <Tab>Entries ({editedPreview.entries.length})</Tab>
                            <Tab>Tags ({editedPreview.stats.tags.length})</Tab>
                            <Tab>Custom Fields ({editedPreview.stats.customFields.length})</Tab>
                        </TabList>

                        <TabPanels>
                            <TabPanel>
                                <VStack align="stretch" spacing={4}>
                                    <Box>
                                        <Text fontWeight="bold">Date Range:</Text>
                                        <Text>
                                            {format(new Date(editedPreview.dateRange.start), 'PPP')} -{' '}
                                            {format(new Date(editedPreview.dateRange.end), 'PPP')}
                                        </Text>
                                    </Box>

                                    <Box>
                                        <Text fontWeight="bold">Total Entries:</Text>
                                        <Text>{editedPreview.stats.totalEntries}</Text>
                                    </Box>

                                    <Box>
                                        <Text fontWeight="bold">Unique Moods:</Text>
                                        <HStack wrap="wrap">
                                            {editedPreview.stats.uniqueMoods.map(mood => (
                                                <Badge key={mood}>{mood}</Badge>
                                            ))}
                                        </HStack>
                                    </Box>
                                </VStack>
                            </TabPanel>

                            <TabPanel>
                                <Box overflowX="auto">
                                    <JournalEntryPreview
                                        entries={editedPreview.entries}
                                        tags={editedPreview.stats.tags}
                                        onEditEntry={handleEditEntry}
                                    />
                                </Box>
                            </TabPanel>

                            <TabPanel>
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Tag Name</Th>
                                            <Th>Color</Th>
                                            <Th>Usage Count</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {editedPreview.stats.tags.map(tag => (
                                            <Tr key={tag.name}>
                                                <Td>
                                                    <Badge colorScheme={tag.color}>{tag.name}</Badge>
                                                </Td>
                                                <Td>
                                                    <HStack spacing={2}>
                                                        <Input
                                                            type="color"
                                                            value={tag.color}
                                                            onChange={(e) => {
                                                                setEditedPreview(prev => ({
                                                                    ...prev,
                                                                    stats: {
                                                                        ...prev.stats,
                                                                        tags: prev.stats.tags.map(t =>
                                                                            t.name === tag.name ? { ...t, color: e.target.value } : t
                                                                        )
                                                                    }
                                                                }));
                                                            }}
                                                            width="100px"
                                                        />
                                                    </HStack>
                                                </Td>
                                                <Td>{tag.entries_count}</Td>
                                                <Td>
                                                    <IconButton
                                                        aria-label="Remove tag"
                                                        icon={<FiTrash2 />}
                                                        colorScheme="red"
                                                        size="sm"
                                                        onClick={() => handleRemoveTag(tag.name)}
                                                    />
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TabPanel>

                            <TabPanel>
                                <Table variant="simple">
                                    <Thead>
                                        <Tr>
                                            <Th>Field Name</Th>
                                            <Th>Type</Th>
                                            <Th>Category</Th>
                                            <Th>Usage Count</Th>
                                            <Th>Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {editedPreview.stats.customFields.map(field => (
                                            <Tr key={field.name}>
                                                <Td>{field.name}</Td>
                                                <Td>{field.field_type}</Td>
                                                <Td>{field.category}</Td>
                                                <Td>{field.entries_count}</Td>
                                                <Td>
                                                    <HStack>
                                                        <IconButton
                                                            aria-label="Edit field"
                                                            icon={<FiEdit2 />}
                                                            size="sm"
                                                            onClick={() => handleEditCustomField(field)}
                                                        />
                                                        <IconButton
                                                            aria-label="Remove field"
                                                            icon={<FiTrash2 />}
                                                            colorScheme="red"
                                                            size="sm"
                                                            onClick={() => handleRemoveCustomField(field.name)}
                                                        />
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </ModalBody>

                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button colorScheme="blue" onClick={() => onConfirm(editedPreview)}>
                        Import Entries
                    </Button>
                </ModalFooter>
            </ModalContent>

            {/* Entry Edit Modal */}
            <Modal isOpen={entryModal.isOpen} onClose={entryModal.onClose} size="2xl">
                <ModalOverlay />
                <ModalContent maxW="800px">
                    <ModalHeader>Edit Entry</ModalHeader>
                    <ModalBody>
                        {selectedEntry && (
                            <JournalEntryForm
                                entry={selectedEntry}
                                onChange={setSelectedEntry}
                                availableTags={editedPreview.stats.tags}
                            />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={entryModal.onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSaveEntry}>
                            Save Changes
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Custom Field Edit Modal */}
            <Modal isOpen={customFieldModal.isOpen} onClose={customFieldModal.onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit Custom Field</ModalHeader>
                    <ModalBody>
                        {selectedCustomField && (
                            <CustomFieldForm
                                field={selectedCustomField}
                                onChange={(field) => setSelectedCustomField(field)}
                                isNameReadOnly={true}
                            />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={customFieldModal.onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSaveCustomField}>
                            Save Changes
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Modal>
    );
};

export default JournalImportPreview; 
